import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import { Customer } from 'src/features/customers/utils/types';
import { customerFormSchema, CustomerFormSchemaValues } from 'src/features/customers/utils/formSchema';
import { useGetCustomer } from 'src/features/customers/api/useGetCustomer';
import CustomerForm from 'src/features/customers/components/forms/CustomerForm';

interface CustomerFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedCustomer?: Customer;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (data: CustomerFormSchemaValues, onSuccess: () => void, onError: (error: GeneralASError) => void) => void;
}

function CustomerFormModal<TData>({
    selectedCustomer,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: CustomerFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    // We are in edit mode if we have a selected service provider
    // otherwise it means we are creating a new service provider

    const { data: customerData } = useGetCustomer({
        id: selectedCustomer?.id ?? '',
        // We only want to fetch if we're in edit
        config: { enabled: !!selectedCustomer?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            name: customerData?.name ?? '',
            uic: customerData?.uic ?? '',
            external_customer_id: customerData?.external_customer_id ?? undefined,
            stripe_customer_id: customerData?.stripe_customer_id ?? undefined,
            address: customerData?.address ?? null,
            tax_registration_number: customerData?.tax_registration_number ?? null,
            representative: customerData?.representative ?? null,
            city: customerData?.city ?? null,
            country: customerData?.country ?? null,
            email: customerData?.email ?? null,
            phone: customerData?.phone ?? null,
            language: customerData?.language ?? undefined,
            configuration: customerData?.configuration ?? null,
        };
    }, [customerData]);

    const form = useForm<CustomerFormSchemaValues>({
        resolver: zodResolver(customerFormSchema),
        defaultValues,
    });

    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        form.reset(defaultValues);
    }, [customerData, isModalOpen, defaultValues, form]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: CustomerFormSchemaValues) => {
            const { language, configuration } = values;
            const payload = {
                ...values,
                configuration: {
                    ...configuration,
                    locale: language,
                },
            };
            mutate(
                { ...payload },
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },

        [mutate, onError, form.setError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <CustomerForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedCustomer && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default CustomerFormModal;
